/* import __COLOCATED_TEMPLATE__ from './driver-inputs.hbs'; */
import Component from '@ember/component';
import { service } from '@ember/service';
import { computed, action } from '@ember/object';
import Constants from '../../utils/constants';

export default class DriverInputs extends Component {
    tagName = '';

    @service('data') model;
    @service selectionData;
    @service driverSupplyService;
    @service driverService;
    @service sigmaDeltaData;

    @service hashService;
    @service hashDataService;
    @service componentManager;
    @service errorHandling;

    panelHeadingBodyArgs = {};
    qaAttrValue = `${Constants.QA_ATTR_PREFIX}_driver_bx`;

    init() {
        super.init();
        this.set('componentManager.driverInputs', this);
    }

    @action showDeviceTable() {
        if (this.openDeviceTable) {
            this.openDeviceTable('driver');
        }
    }

    @action openCardTooltip() {
        if (this.openHelp) {
            this.openHelp(`info_modal.driver_input.title`, `info_modal.driver_input.body`, {
                ...this.panelHeadingBodyArgs,
            });
        }
    }

    @action onDriverVariantChange() {
        this.model.switchVariant('driver');
        this.driverService.setTopologyStatus();

        this.driverService.defaultRf();
        this.driverService.resetGain();
        this.driverService.resetRf();
        this.driverService.defaultCf();
        this.driverService.resetCf();

        this.driverSupplyService.calculateVSupply();

        // Set url parameters
        this.hashDataService.setUrlParameterWithSelectedPart('driver');
        this.hashDataService.setUrlParameter('model.showDriverVariant');
        this.hashDataService.setUrlParameterForObject('showDriverVariants');
        this.hashDataService.setUrlParameterWithSelectedId('topology');
        this.hashDataService.setUrlParameter('model.vSupplyPos');
        this.hashDataService.setUrlParameter('model.vSupplyNeg');

        this.hashDataService.setUrlParameter('model.rf');
        this.hashDataService.setUrlParameter('model.gain');

        if (this.updateAppData) this.updateAppData();
    }

    @action onSelectedTopologyChange() {
        this.driverService.setTopologyStatus();
        this.driverService.resetGain();
        this.driverService.resetRf();
        this.driverService.defaultCf();
        this.driverService.resetCf();
        this.driverSupplyService.calculateVSupply();

        this.hashDataService.setUrlParameter('model.rf');
        this.hashDataService.setUrlParameter('model.gain');
        this.hashDataService.setUrlParameter('model.cf');

        this.hashDataService.setUrlParameterWithSelectedId('topology');
        this.hashDataService.setUrlParameter('model.vSupplyPos');
        this.hashDataService.setUrlParameter('model.vSupplyNeg');

        if (this.updateAppData) this.updateAppData();
    }

    @action onGainChange() {
        this.driverService.setTopologyStatus();
        this.driverService.resetRf();
        this.driverService.defaultCf();
        this.driverService.resetCf();

        this.driverSupplyService.calculateVSupply();

        this.hashDataService.setUrlParameter('model.rf');
        this.hashDataService.setUrlParameter('model.gain');
        this.hashDataService.setUrlParameterWithSelectedId('topology');
        this.hashDataService.setUrlParameter('model.vSupplyPos');
        this.hashDataService.setUrlParameter('model.vSupplyNeg');

        if (this.updateAppData) this.updateAppData();
    }

    @action onRfChange() {
        this.driverSupplyService.calculateVSupply();

        this.hashDataService.setUrlParameter('model.rf');

        this.hashDataService.setUrlParameter('model.vSupplyPos');
        this.hashDataService.setUrlParameter('model.vSupplyNeg');

        if (this.updateAppData) this.updateAppData();
    }

    @action onCfChange() {
        this.hashDataService.setUrlParameter('model.cf');
        if (this.updateAppData) this.updateAppData();
    }

    @action onVSupplyPosChange() {
        this.driverSupplyService.makeRoundingModifications();
        this.hashDataService.setUrlParameter('model.vSupplyPos');
        this.hashDataService.setUrlParameter('model.vSupplyNeg');

        if (this.updateAppData) this.updateAppData();
    }

    @action onVSupplyNegChange() {
        this.driverSupplyService.makeRoundingModifications();
        this.hashDataService.setUrlParameter('model.vSupplyPos');
        this.hashDataService.setUrlParameter('model.vSupplyNeg');

        if (this.updateAppData) this.updateAppData();
    }

    /**
     * These should be the same for all variant codes
     * Variations go in bodyArgsByVariant
     */
    @computed('selectionData.selectedDriver.{partCore,datasheet}') get variantModalBodyArgs() {
        return {
            driver: this.selectionData.selectedDriver.partCore,
            datasheet: this.selectionData.selectedDriver.datasheet,
            lastUpdateData: new Date(),
        };
    }

    /**
     * Can only be used in initialLoadWithHash
     */
    canCalculateProperty(propertyName) {
        let hashValues = this.hashService.parsedLocationHash;
        return !(hashValues[propertyName] && Number(hashValues[propertyName]) !== 0);
    }

    initialLoadWithHash() {
        // Make sure all the data is loaded
        this.model.switchVariant('driver');

        this.driverService.setTopologyStatus(this.canCalculateProperty('selectedTopology'));
        this.driverService.defaultRf(this.canCalculateProperty('rf'));
        this.driverService.resetGain(this.canCalculateProperty('gain'));
        this.driverService.resetRf(this.canCalculateProperty('rf'));
        this.driverService.defaultCf(this.canCalculateProperty('cf'));
        this.driverService.resetCf(this.canCalculateProperty('cf'));

        if (this.canCalculateProperty('vSupplyPos') && this.canCalculateProperty('vSupplyNeg')) {
            this.driverSupplyService.calculateVSupply();
        }

        // Set Url Parameters
        this.hashDataService.setUrlParameter('model.rf');
        this.hashDataService.setUrlParameter('model.gain');
        this.hashDataService.setUrlParameterWithSelectedPart('driver');
        this.hashDataService.setUrlParameter('model.showDriverVariant');
        this.hashDataService.setUrlParameterForObject('showDriverVariants');
        this.hashDataService.setUrlParameterWithSelectedId('topology');
        this.hashDataService.setUrlParameter('model.vSupplyPos');
        this.hashDataService.setUrlParameter('model.vSupplyNeg');
    }

    initialLoadWithoutHash() {
        // Make sure all the data is loaded
        this.model.updateShowVariant('driver');
        this.model.switchVariant('driver');
        this.driverService.setTopologyStatus();
        this.driverService.defaultRf();
        this.driverService.resetGain();
        this.driverService.resetRf();
        this.driverService.defaultCf();
        this.driverService.resetCf();

        this.driverSupplyService.calculateVSupply();

        // Set Url Parameters
        this.hashDataService.setUrlParameter('model.rf');
        this.hashDataService.setUrlParameter('model.gain');
        this.hashDataService.setUrlParameterWithSelectedPart('driver');
        this.hashDataService.setUrlParameter('model.showDriverVariant');
        this.hashDataService.setUrlParameterForObject('showDriverVariants');
        this.hashDataService.setUrlParameterWithSelectedId('topology');
        this.hashDataService.setUrlParameter('model.vSupplyPos');
        this.hashDataService.setUrlParameter('model.vSupplyNeg');
    }
}
